import typeAssert, { DataObject } from "../utils/typeAssert.util";
import { FunctionResolver } from "./index";
import { ModifiedWebSocket } from "../utils/websocketModifier.util";
import { ModelResolver } from "../modelResolvers";
import { IdResolver } from "../idResolvers";
import InvertedWeakMap from "./utils/invertedWeakMap.util";
import { FunctionResolverFunction } from "../types";

const CONNECTION_LOST = "CONNECTION_LOST";

export default class WeakFunctionPool extends FunctionResolver {
    private isSessionActive = true;

    static typeName() {
        return "WeakFunctionPool";
    }

    private readonly timeoutSize: number;

    private readonly oursFunctions: Map<string, Function>;

    private readonly theirsFunctionsWaitPool: Map<string, Function>;

    private theirsFunctionsIds: string[];

    private theirsFunctions: InvertedWeakMap;

    private findUnusedTimeout: ReturnType<typeof setTimeout> | null;

    constructor(options: {
        session: ModifiedWebSocket;
        sendMessage: (message: any) => any;
        deSerializeObject: ModelResolver["deserialize"];
        serializeObject: ModelResolver["serialize"];
        uuid: IdResolver["gen"];
    }) {
        super(options);
        // ours — локальные функции по id; theirs — прокси на удалённые (WeakMap).
        this.oursFunctions = new Map();
        this.theirsFunctions = new InvertedWeakMap();
        this.theirsFunctionsIds = [];
        this.theirsFunctionsWaitPool = new Map();
        this.timeoutSize = 600 * 1000;

        // Периодически сказать соседу забыть функции, которые уже никто не держит.
        this.findUnusedTimeout = setTimeout(
            function findUnused(this: WeakFunctionPool) {
                this.findUnusedFunctions();
                if (this.isSessionActive) {
                    this.findUnusedTimeout = setTimeout(
                        findUnused.bind(this),
                        this.timeoutSize
                    );
                }
            }.bind(this),
            this.timeoutSize
        );

        // При обрыве WS — отклонить все ожидающие удалённые вызовы.
        this.options.session.addEventListener("close", ({ code, reason }) => {
            const err = new Error(
                `Session Connection was closed with code "${code}" ${
                    reason ? `and message: "${reason}"` : ""
                }`
            );
            (err as any).type = CONNECTION_LOST;
            (err as any).__from = "theirs";
            this.theirsFunctionsWaitPool.forEach((item) => item(err));
            this.theirsFunctionsWaitPool.clear();
            this.isSessionActive = false;
            if (this.findUnusedTimeout) {
                clearTimeout(this.findUnusedTimeout);
                this.findUnusedTimeout = null;
            }
        });
    }

    private messageBuilder = (type: string, requestId: string, data: any) => ({
        type,
        requestId,
        data,
    });

    private baseMessageBuilder = (type: string, data: any) => ({
        type,
        data,
    });

    executeFunctionCatcher = async (executor: Function, ...params: any[]) => {
        try {
            return executor(...params);
        } catch (e: any) {
            e.__from = "theirs";
            e.type = "unexpected";
            return e;
        }
    };

    public async onMessage(message: DataObject) {
        typeAssert(
            message,
            {
                clear: () => {
                    // Сосед больше не держит эти id — убрать локальные обработчики.
                    message.data.ids.forEach((id: string) => {
                        this.oursFunctions.delete(id);
                    });
                },
                execute: async () => {
                    // Удалённый вызов: id функции → deserialize args → выполнить → serialize ответ.
                    try {
                        const executor = await this.getOurs(message.data.id);
                        if (!executor) {
                            return;
                        }
                        await this.options.sendMessage(
                            this.messageBuilder(
                                "executeResponse",
                                message.requestId,
                                {
                                    id: message.data.id,
                                    payload: await this.options.serializeObject(
                                        await this.executeFunctionCatcher(
                                            executor,
                                            ...(await this.options.deSerializeObject(
                                                message.data.payload,
                                                this.setTheirs.bind(this)
                                            ))
                                        ),
                                        this.setOurs.bind(this)
                                    ),
                                }
                            )
                        );
                    } catch (e) {
                        // keep the session; caller already gets no response
                    }
                },
                executeResponse: async () => {
                    // Ответ на наш вызов: найти waiter по requestId и отдать результат.
                    const waiter = this.theirsFunctionsWaitPool.get(
                        message.requestId
                    );
                    if (!waiter) {
                        return;
                    }
                    waiter(
                        await this.options.deSerializeObject(
                            message.data.payload,
                            this.setTheirs.bind(this)
                        )
                    );
                    this.theirsFunctionsWaitPool.delete(message.requestId);
                },
            },
            () => {
                throw new Error(
                    `Could not understand request. ${
                        message.type
                            ? `Type is "${message.type}"`
                            : "No type presented"
                    }`
                );
            }
        );
    }

    async setOurs(executor: Function, id?: string) {
        // Зарегистрировать локальную функцию и вернуть id для сериализации модели.
        const ident = id || (await this.options.uuid());
        this.oursFunctions.set(ident, executor);
        return ident;
    }

    async setTheirs(id: string) {
        // Прокси: при вызове сериализовать аргументы и ждать executeResponse.
        this.theirsFunctions.set(
            id,
            async (...params: any[]) =>
                new Promise(async (resolve, reject) => {
                    const requestId = await this.options.uuid();
                    this.theirsFunctionsWaitPool.set(
                        requestId,
                        (response: any) => {
                            if (
                                typeof response === "object" &&
                                (response.type === "unexpected" ||
                                    response.type === CONNECTION_LOST) &&
                                response.__from === "theirs"
                            ) {
                                reject(response);
                            } else {
                                resolve(response);
                            }
                        }
                    );
                    try {
                        await this.options.sendMessage(
                            this.messageBuilder("execute", requestId, {
                                id,
                                payload: await this.options.serializeObject(
                                    params,
                                    this.setOurs.bind(this)
                                ),
                            })
                        );
                    } catch (e: any) {
                        this.theirsFunctionsWaitPool.delete(requestId);
                        reject(e);
                    }
                })
        );
        this.theirsFunctionsIds.push(id);
        return this.theirsFunctions.get(
            id
        ) as Promise<FunctionResolverFunction>;
    }

    public findUnusedFunctions() {
        const staleIds = this.theirsFunctionsIds.filter(
            (id) => !this.theirsFunctions.has(id)
        );
        if (staleIds.length) {
            this.theirsFunctionsIds = this.theirsFunctionsIds.filter((id) =>
                this.theirsFunctions.has(id)
            );
            this.sendClearFunction(staleIds);
        }
    }

    private async sendClearFunction(functionIds: string[]) {
        try {
            await this.options.sendMessage(
                this.baseMessageBuilder("clear", { ids: functionIds })
            );
        } catch (e) {
            // ignore: session may already be closed
        }
    }

    async getOurs(id: string) {
        return this.oursFunctions.get(id) || null;
    }

    async getTheirs(id: string) {
        return this.theirsFunctions.has(id)
            ? ((await this.theirsFunctions.get(id)) as FunctionResolverFunction)
            : null;
    }

    close() {
        this.isSessionActive = false;
        if (this.findUnusedTimeout) {
            clearTimeout(this.findUnusedTimeout);
            this.findUnusedTimeout = null;
        }
        this.oursFunctions.clear();
        this.theirsFunctionsWaitPool.clear();
        this.theirsFunctionsIds = [];
    }
}
